import { formatMoney, formatQuantityWithUnit } from "./utils";
import {
  formatQuantityTotals,
  PeriodDemand,
} from "./demandForecastCalendarUtils";

/**
 * Renders a detailed card for one period, listing up to six weighted forecast
 * segments with their description, location, quantity, and reasoning.
 * @param period - The period's aggregated demand and segments.
 * @returns The period breakdown card.
 */
export function PeriodBreakdown({ period }: { period: PeriodDemand }) {
  const visibleSegments = period.segments.slice(0, 6);
  const hiddenSegmentCount = period.segments.length - visibleSegments.length;

  return (
    <div className="min-w-0 overflow-hidden rounded-md border bg-card p-4 text-card-foreground">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="break-words font-medium">{period.label}</div>
          <div className="break-words text-xs text-muted-foreground">
            {formatQuantityTotals(period.weightedQuantityTotals)} weighted
          </div>
        </div>
        <div className="shrink-0 font-semibold tabular-nums sm:text-right">
          {formatMoney(period.weightedRevenue)}
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {visibleSegments.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No forecast demand
          </div>
        ) : (
          visibleSegments.map((segment) => (
            <div
              key={segment.key}
              className="flex min-w-0 flex-col gap-2 overflow-hidden rounded-sm bg-muted/30 px-3 py-2 text-sm sm:flex-row sm:items-center sm:gap-3"
            >
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <span
                  className="mt-1 h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: segment.color }}
                />
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 break-words font-medium [overflow-wrap:anywhere]">
                    {segment.label}
                  </div>
                  <div className="line-clamp-2 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
                    {segment.detail}
                    {segment.locationName ? ` - ${segment.locationName}` : ""}
                  </div>
                  <div className="break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
                    {formatQuantityWithUnit(
                      String(segment.weightedQuantity),
                      segment.quantityUnit,
                    )}{" "}
                    weighted
                  </div>
                  {segment.reasoning && (
                    <div className="line-clamp-2 break-words text-xs italic text-muted-foreground/80 [overflow-wrap:anywhere]">
                      {segment.reasoning}
                    </div>
                  )}
                </div>
              </div>
              <div className="shrink-0 pl-5 text-sm tabular-nums sm:pl-0 sm:text-right">
                {formatMoney(segment.weightedRevenue)}
              </div>
            </div>
          ))
        )}
        {hiddenSegmentCount > 0 && (
          <div className="rounded-sm bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            +{hiddenSegmentCount} more
          </div>
        )}
      </div>
    </div>
  );
}
